/**
 * EO (Executive Office) — tabbed shell. Wired into RoleDashboard
 * (`if (role === "EO") return <EoScreen/>`). EO accent = фуксия (--eo-pink via
 * <html data-role="eo">). Tabs:
 *   • «Финансы»     — факт+прогноз выручки за период (EoFinanceView, perm
 *                     finance:read; V1 FinAnalyticsView). Default-вкладка.
 *   • «Одобрение»   — общая панель «Бот для клиентов» (лента изменений +
 *                     аккаунты операторов), переиспользуется из OO (§0.1.3 —
 *                     одобряют и OO, и EO; perm actor_approval).
 *   • «Ставки»      — сетка ставок (A16 — EO-owned) + ведомость зарплат,
 *                     через общий OoSalaryView (route-gated OO/EO).
 *   • «Конфигуратор» — параметры филиалов + справочник чайных (EoConfigView,
 *                     эталон screen-16; PUT branches — perm branches.write,
 *                     EO-only). DateSelector не нужен (не в isRanged).
 *   • «Юникоины»    — UC-панель staff-экономики (EoUcView, эталон screen-22;
 *                     остаток E4, A26). Период — свой чип-пресет внутри
 *                     вкладки (УД2), не DateSelector шапки.
 *   • «Аудит»       — лог действий сотрудников + сводки (EoAuditView, perm
 *                     audit:read; V1 AuditView). EO видит и EO-активность.
 *   • «Бэкап медиа» — экспорт манифеста + поимённая выгрузка (§0.1.2, perm
 *                     media_backup; EO-only).
 *   • «Загрузка 1С» — 20.10: код загрузчика архива 1С → «Разрешить», состояние
 *                     загрузки, «Закрыть доступ» (Archive1cPanel; EO-only).
 *   • «Виджет»      — 01.11: раздел виджета заявок v2 (EoWidgetView, ТЗ EMB-019):
 *                     сайты, код установки, «Работает ли», аналитика, аномалии.
 *
 * Header: для range-вкладок (Финансы, Аудит) DateSelector свапается в
 * mode="range" (паттерн DcGridScreen); стейт периода — business UTC-midnight Date, вниз
 * уходят строки YYYY-MM-DD + onSetRange (пресеты синхронизируют лейбл
 * триггера через императивный window.DateSelector.setRange).
 *
 * Shell mirrors the OO board (AppShell + MarqueeHeader + icon sec-nav), so the
 * role token recolours the same markup. No 100dvh here — AppShell owns layout.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement, type SVGProps } from "react";
import { AppShell, MarqueeHeader } from "../../layout";
import { DateSelector, type DateRange } from "../../components/DateSelector";
import { useAuth } from "../../auth";
import { ClientBotPanel } from "../oo/ClientBotPanel";
import { OoSalaryView } from "../oo/OoSalaryView";
import { useActorChanges } from "../../hooks/useActorChanges";
import { NavBot, NavBase, Money, Eye } from "../oo/v8icons";
import { Coin } from "../oo/finIcons";
import { MediaBackup } from "./MediaBackup";
import { RateRestorePanel } from "./RateRestorePanel";
import { KvestgidOriginPanel } from "./KvestgidOriginPanel";
import { Archive1cPanel } from "./Archive1cPanel";
import { StaffRoster } from "../hr/StaffRoster";
import { EoFinanceView } from "./EoFinanceView";
import { EoAuditView } from "./EoAuditView";
import { EoConfigView } from "./EoConfigView";
import { EoUcView } from "./EoUcView";
import { EoWidgetView } from "./widget/EoWidgetView";
import { fmtDateApi, fromBusinessDate, parseDateApi, toBusinessDate, todayBusiness } from "../../utils/date";
import css from "./eo.module.css";

type ViewId = "finance" | "approval" | "salary" | "config" | "uc" | "audit" | "backup" | "hr" | "a1c" | "widget";
type IconC = (p: SVGProps<SVGSVGElement>) => ReactElement;

/** Слайдеры-конфиг — глифика вкладки из эталона screen-16 (sec-nav). */
const NavConfig: IconC = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M3 7h9M18 7h3M3 17h5M14 17h7" />
    <path d="M12 4.5h6v5h-6zM8 14.5h6v5H8z" />
  </svg>
);

/** Монета-UC — глифика вкладки «Юникоины» из эталона screen-22 (sec-nav). */
const NavUc: IconC = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.2 8.5v4a2.6 2.6 0 0 0 5.2 0v-4M15.8 15.5a3.5 3.5 0 0 1 0-7" />
  </svg>
);

/** Человечек-роли — глифика вкладки HR. */
const NavHr: IconC = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" {...p}>
    <circle cx="9" cy="8" r="3.4" />
    <path d="M3.5 20c.6-3.6 2.9-5.4 5.5-5.4s4.9 1.8 5.5 5.4" />
    <path d="M16 5.5l1 2 2.2.3-1.6 1.5.4 2.2-2-1-2 1 .4-2.2-1.6-1.5 2.2-.3z" />
  </svg>
);

/** Цилиндр базы со стрелкой внутрь — вкладка «Загрузка 1С». */
const NavA1c: IconC = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...p}>
    <ellipse cx="12" cy="6" rx="7" ry="2.6" />
    <path d="M5 6v12c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V6M12 10v6M9.5 13.5 12 16l2.5-2.5" />
  </svg>
);

/** Окно с курсором-кликом — вкладка «Виджет» (заявки с сайтов). */
const NavWidget: IconC = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...p}>
    <rect x="3" y="4" width="18" height="13" rx="1.5" />
    <path d="M3 8h18M13 13l6 6M13 13v5l1.6-1.6M13 13h5l-1.6 1.6" />
  </svg>
);

const TABS: { id: ViewId; label: string; Icon: IconC }[] = [
  { id: "finance", label: "Финансы", Icon: Money },
  { id: "approval", label: "Одобрение", Icon: NavBot },
  { id: "hr", label: "HR", Icon: NavHr },
  { id: "salary", label: "Ставки", Icon: Coin },
  { id: "config", label: "Конфигуратор", Icon: NavConfig },
  { id: "uc", label: "Юникоины", Icon: NavUc },
  { id: "audit", label: "Аудит", Icon: Eye },
  { id: "backup", label: "Бэкап медиа", Icon: NavBase },
  { id: "a1c", label: "Загрузка 1С", Icon: NavA1c },
  { id: "widget", label: "Виджет", Icon: NavWidget },
];

/** Текущий месяц (UTC+5) как business-диапазон — дефолт «Финансов». */
function currentMonthRange(): DateRange {
  const t = todayBusiness();
  return {
    start: new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 1)),
    end: new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)),
  };
}

export function EoScreen() {
  const { user } = useAuth();
  const [view, setView] = useState<ViewId>("finance");
  // Pending-count badge on the «Одобрение» tab (shared feed hook).
  const { count } = useActorChanges();
  /* Периоды range-вкладок — business UTC-midnight (см. utils/date.ts). */
  const [finRange, setFinRange] = useState<DateRange>(currentMonthRange);
  const [auditRange, setAuditRange] = useState<DateRange>(() => ({ start: todayBusiness(), end: todayBusiness() }));

  useEffect(() => {
    document.documentElement.dataset.role = "eo";
  }, []);

  const isRanged = view === "finance" || view === "audit";
  const range = view === "audit" ? auditRange : finRange;
  /* Локальная (для календаря DateSelector) версия business-диапазона. */
  const rangeLocal = useMemo<DateRange>(() => ({
    start: range.start ? fromBusinessDate(range.start) : null,
    end: range.end ? fromBusinessDate(range.end) : null,
  }), [range.start, range.end]);
  const from = range.start ? fmtDateApi(range.start) : "";
  const to = range.end ? fmtDateApi(range.end) : "";

  /* Выбор в календаре: коммитим только полный диапазон (start+end). */
  const onRange = useCallback((value: Date | DateRange) => {
    if (value instanceof Date || !value.start || !value.end) return;
    const next = { start: toBusinessDate(value.start), end: toBusinessDate(value.end) };
    (view === "audit" ? setAuditRange : setFinRange)(next);
  }, [view]);

  /* Пресеты внутри вкладок: стейт + синхронизация лейбла триггера (M.1 API). */
  const onSetRange = useCallback((f: string, t: string) => {
    const next = { start: parseDateApi(f), end: parseDateApi(t) };
    (view === "audit" ? setAuditRange : setFinRange)(next);
    window.DateSelector?.setRange(fromBusinessDate(next.start), fromBusinessDate(next.end));
  }, [view]);

  const nick = user?.nickname || user?.name || "guest";

  return (
    <AppShell
      lockViewport={view === "config" || view === "hr" || view === "widget"} /* конфигуратор, HR и «Виджет» скроллят своей панелью (24.08; HR — 03.10; виджет — 01.11) */
      header={
        <MarqueeHeader
          roleLabel="EXECUTIVE OFFICE"
          nick={nick}
          dateSelector={isRanged
            ? <DateSelector key={view} mode="range" initialRange={rangeLocal} onChange={onRange} />
            : <DateSelector key="eo-day" mode="day" />}
        />
      }
      topZone={
        <nav className={css.secNav}>
          {TABS.map(({ id, label, Icon }) => {
            const active = view === id;
            return (
              <button
                key={id}
                type="button"
                className={`${css.snav} ${active ? css.active : ""}`}
                aria-label={label}
                onClick={() => setView(id)}
              >
                <span className={css.ti}><Icon aria-hidden="true" /></span>
                <span className={css.tw}>{label}</span>
                {id === "approval" && count > 0 ? <span className={css.badge}>{count}</span> : null}
              </button>
            );
          })}
        </nav>
      }
    >
      <div className={css.body}>
        {view === "finance" ? (
          <EoFinanceView from={from} to={to} onSetRange={onSetRange} />
        ) : view === "audit" ? (
          <EoAuditView from={from} to={to} onSetRange={onSetRange} />
        ) : view === "approval" ? (
          <ClientBotPanel />
        ) : view === "backup" ? (
          <><RateRestorePanel /><KvestgidOriginPanel /><MediaBackup /></>
        ) : view === "a1c" ? (
          <Archive1cPanel />
        ) : view === "hr" ? (
          <StaffRoster mode="eo" />
        ) : view === "config" ? (
          <EoConfigView />
        ) : view === "uc" ? (
          <EoUcView />
        ) : view === "widget" ? (
          <EoWidgetView />
        ) : (
          <OoSalaryView initialTab="rates" ratesEditable />
        )}
      </div>
    </AppShell>
  );
}
